// Skills: named instructions an admin writes for the studio assistant, like Claude Code skills. The assistant sees
// each enabled skill's name and description, and loads the full instructions when a request matches.
import { z } from "zod";

export const SKILL_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,47}$/;

export const SkillName = z
  .string()
  .trim()
  .toLowerCase()
  .regex(SKILL_NAME_PATTERN, "Skill names have 2–48 lowercase letters, digits or dashes, like minimax-video-prompts.");

export const Skill = z.object({
  id: z.string(),
  name: z.string(),
  /** When the assistant should use it; the assistant sees this line for every enabled skill. */
  description: z.string(),
  /** The instructions themselves (Markdown), loaded by the assistant when it uses the skill. */
  content: z.string(),
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Display name of the admin who changed it last. */
  updatedBy: z.string().nullable(),
});
export type Skill = z.infer<typeof Skill>;

export const CreateSkillBody = z.object({
  name: SkillName,
  description: z
    .string()
    .trim()
    .min(10, "Describe in a sentence when the assistant should use this skill.")
    .max(400, "Keep the description under 400 characters; the details go into the instructions."),
  content: z
    .string()
    .trim()
    .min(1, "Write the instructions.")
    .max(40_000, "Instructions can have at most 40,000 characters."),
  enabled: z.boolean().default(true),
});
export type CreateSkillBody = z.infer<typeof CreateSkillBody>;
export type CreateSkillInput = z.input<typeof CreateSkillBody>;

export const UpdateSkillBody = CreateSkillBody.partial();
export type UpdateSkillBody = z.infer<typeof UpdateSkillBody>;

/** Input of the assistant's load_skill tool. */
export const LoadSkillInput = z.object({
  name: z.string().describe("Name of the skill to load, exactly as listed under Skills."),
});
